/** Preferência persistente para cor do tema (`class="dark"` no `<html>`). */
export type ThemePreference = 'system' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'metricaleiloes-theme';
const LEGACY_DARK_FLAG = 'metricaleiloes-dark-mode';

function syncColorSchemeMeta(dark: boolean): void {
  if (typeof document === 'undefined') return;
  let meta = document.querySelector('meta[name="color-scheme"]') as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'color-scheme';
    document.head.insertBefore(meta, document.head.firstChild);
  }
  meta.content = dark ? 'dark' : 'light';
}

function syncBodySurface(dark: boolean): void {
  if (typeof document === 'undefined') return;
  const body = document.body;
  if (!body) return;
  if (dark) {
    body.style.removeProperty('background-color');
    body.style.removeProperty('color');
    return;
  }
  body.style.backgroundColor = '#fcfcfc';
  body.style.color = '#1e293b';
}

/** Lê valor salvo ou migra flag antiga só-booleano. Default: claro. */
export function readThemePreference(): ThemePreference {
  if (typeof window === 'undefined') return 'light';

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;

    const leg = window.localStorage.getItem(LEGACY_DARK_FLAG);
    if (leg === 'true') {
      window.localStorage.setItem(THEME_STORAGE_KEY, 'dark');
      return 'dark';
    }
    if (leg === 'false') {
      window.localStorage.setItem(THEME_STORAGE_KEY, 'light');
      return 'light';
    }
  } catch {
    /* ignorar */
  }
  return 'light';
}

/** Se o modo escuro visual deve estar ativo **agora** (respeita `system`). */
export function resolveDarkActive(pref: ThemePreference): boolean {
  if (pref === 'dark') return true;
  if (pref === 'light') return false;
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

/** Aplica tema na raiz: classe `dark`, `data-theme` resolvido e `color-scheme`. */
export function applyThemeToDocument(pref: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const dark = resolveDarkActive(pref);
  const resolvedTheme = dark ? 'dark' : 'light';
  const colorScheme = dark ? 'dark' : 'only light';

  const hasDark = root.classList.contains('dark');
  if (
    hasDark === dark &&
    root.dataset.theme === resolvedTheme &&
    root.style.colorScheme === colorScheme
  ) {
    syncColorSchemeMeta(dark);
    syncBodySurface(dark);
    return;
  }

  root.classList.toggle('dark', dark);
  root.classList.toggle('light', !dark);
  root.dataset.theme = resolvedTheme;
  root.dataset.themePref = pref;
  root.style.colorScheme = colorScheme;
  syncColorSchemeMeta(dark);
  syncBodySurface(dark);
}

/** Persiste e aplica tema (aplica mesmo se localStorage falhar). */
export function persistThemePreference(pref: ThemePreference): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, pref);
    window.localStorage.removeItem(LEGACY_DARK_FLAG);
  } catch {
    /* quota / modo privado */
  }
  applyThemeToDocument(pref);
}

/** Alterna aparência clara ↔ escura (ignora modo sistema na UI). */
export function toggleLightDarkPreference(current: ThemePreference): ThemePreference {
  return resolveDarkActive(current) ? 'light' : 'dark';
}

/** Estado inicial do cliente — alinhado ao bootstrap no `<head>`. */
export function readClientThemeState(): { pref: ThemePreference; resolvedDark: boolean } {
  const pref = readThemePreference();
  return { pref, resolvedDark: resolveDarkActive(pref) };
}

/** Reaplica se Chrome/React alterou `class` ou `data-theme` do `<html>`. */
export function ensureThemeOnDocument(pref: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const dark = resolveDarkActive(pref);
  const resolvedTheme = dark ? 'dark' : 'light';
  const colorScheme = dark ? 'dark' : 'only light';
  const ok =
    root.classList.contains('dark') === dark &&
    root.dataset.theme === resolvedTheme &&
    root.style.colorScheme === colorScheme;
  if (!ok) applyThemeToDocument(pref);
}
